import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getCustomers } from '../api';
import api from '../api';

const COLORS = [
  '#378ADD', '#1D9E75', '#D85A30', '#BA7517',
  '#7F77DD', '#D4537E', '#639922', '#E24B4A',
];

export default function DbSizes({ dimension, activeOnly }) {
  const [customers, setCustomers]   = useState([]);
  const [selected, setSelected]     = useState('');
  const [data, setData]             = useState(null);
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  useEffect(() => {
    getCustomers(activeOnly).then(r => setCustomers(r.data.filter(c => c.is_active)));
    api.get(`/api/v1/dbsizes/summary?dimension=${dimension}`)
      .then(r => setSummary(r.data))
      .catch(() => setSummary(null));
  }, [dimension, activeOnly]);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api.get(`/api/v1/dbsizes/${selected}?dimension=${dimension}`)
      .then(r => { setData(r.data); setError(null); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [selected, dimension]);

  // Build chart data — one bar per instance, grouped by month
  const instances = data
    ? [...new Set(data.months.flatMap(m => m.instances.map(i => i.db_instance)))]
    : [];

  const chartData = (data?.months || []).map(m => {
    const entry = {
      month: new Date(m.report_date).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
      total: m.total_gb,
    };
    m.instances.forEach(i => {
      entry[i.db_instance] = parseFloat(i.totalsize_gb.toFixed(2));
    });
    return entry;
  });

  // Latest month breakdown table
  const latestMonth = data?.months?.[data.months.length - 1];

  return (
    <>
      {/* Summary metrics */}
      {summary && (
        <div className="metrics" style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))' }}>
          <div className="metric">
            <div className="metric-label">Total storage (all customers)</div>
            <div className="metric-value">{summary.total_gb.toLocaleString()} GB</div>
            <div className="metric-sub">latest report date</div>
          </div>
          <div className="metric">
            <div className="metric-label">Customers with data</div>
            <div className="metric-value">{summary.customer_count}</div>
          </div>
          <div className="metric">
            <div className="metric-label">Largest customer</div>
            <div className="metric-value" style={{ fontSize: 16 }}>
              {summary.customers?.[0]?.cust_name || '—'}
            </div>
            <div className="metric-sub">
              {summary.customers?.[0]?.total_gb?.toLocaleString()} GB
            </div>
          </div>
        </div>
      )}

      {/* Customer selector */}
      <div style={{ marginBottom: 16 }}>
        <select
          style={{ fontSize: 13, padding: '7px 12px', border: '1px solid #e5e5e5', borderRadius: 6, minWidth: 280 }}
          value={selected}
          onChange={e => setSelected(e.target.value)}
        >
          <option value="">Select a customer...</option>
          {[...customers].sort((a, b) => a.cust_name.localeCompare(b.cust_name)).map(c => (
            <option key={c.cust_code} value={c.cust_code}>{c.cust_name}</option>
          ))}
        </select>
      </div>

      {!selected && (
        <div className="loading">Select a customer to view database size history</div>
      )}

      {loading && <div className="loading">Loading...</div>}
      {error   && <div className="error">Error: {error}</div>}

      {data && !loading && (
        <>
          {/* Metrics */}
          <div className="metrics" style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))' }}>
            <div className="metric">
              <div className="metric-label">Current total size</div>
              <div className="metric-value">
                {latestMonth?.total_gb?.toLocaleString()} GB
              </div>
              <div className="metric-sub">{latestMonth?.report_date}</div>
            </div>
            <div className="metric">
              <div className="metric-label">Environments</div>
              <div className="metric-value">{instances.length}</div>
              <div className="metric-sub">instances tracked</div>
            </div>
            <div className="metric">
              <div className="metric-label">Months of data</div>
              <div className="metric-value">{data.month_count}</div>
            </div>
          </div>

          {/* Bar chart — total GB per instance over time */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">
                {data.customer?.cust_name} — database size over time (GB)
              </div>
              <div className="legend">
                {instances.map((inst, i) => (
                  <span key={inst}>
                    <span className="legend-dot" style={{ background: COLORS[i % COLORS.length] }}></span>
                    {inst}
                  </span>
                ))}
              </div>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={v => `${v}GB`}
                  />
                  <Tooltip formatter={(v, name) => [`${v} GB`, name]} />
                  {instances.map((inst, i) => (
                    <Bar
                      key={inst}
                      dataKey={inst}
                      stackId="a"
                      fill={COLORS[i % COLORS.length]}
                      radius={i === instances.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Latest month breakdown table */}
          {latestMonth && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">
                  Latest breakdown — {latestMonth.report_date}
                </div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Instance</th>
                    <th>Region</th>
                    <th>Log size (MB)</th>
                    <th>Row size (MB)</th>
                    <th>Total (MB)</th>
                    <th>Total (GB)</th>
                  </tr>
                </thead>
                <tbody>
                  {latestMonth.instances
                    .sort((a, b) => b.totalsize_gb - a.totalsize_gb)
                    .map((inst, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 500 }}>{inst.db_instance}</td>
                      <td style={{ color: '#666' }}>{inst.region}</td>
                      <td>{inst.logsize_mb.toLocaleString()}</td>
                      <td>{inst.rowsize_mb.toLocaleString()}</td>
                      <td>{inst.totalsize_mb.toLocaleString()}</td>
                      <td style={{ fontWeight: 500 }}>{inst.totalsize_gb.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '2px solid #e5e5e5' }}>
                    <td colSpan={5} style={{ fontWeight: 500, textAlign: 'right' }}>Total</td>
                    <td style={{ fontWeight: 500 }}>{latestMonth.total_gb.toLocaleString()} GB</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Full history table */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Monthly size history</div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Total (GB)</th>
                  <th>Instances</th>
                </tr>
              </thead>
              <tbody>
                {[...data.months].reverse().map((m, i) => (
                  <tr key={i}>
                    <td>{new Date(m.report_date).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</td>
                    <td style={{ fontWeight: 500 }}>{m.total_gb.toLocaleString()} GB</td>
                    <td style={{ color: '#666', fontSize: 12 }}>
                      {m.instances.map(i => i.db_instance).join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
